/**
 * Admin Session Security Settings
 * ================================
 * Super Admin only page to configure banking-grade security settings:
 * - Access token expiry time
 * - Idle timeout duration
 * - Sudo mode duration
 * - Force re-login interval
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { 
  Shield, 
  Clock, 
  Key, 
  RefreshCw, 
  Save, 
  AlertTriangle,
  Timer,
  Lock,
  Info
} from 'lucide-react';

const AdminSessionSecurity = () => {
  const { apiCall, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    access_token_expiry_minutes: 15,
    refresh_token_expiry_hours: 12,
    idle_timeout_minutes: 30,
    sudo_token_expiry_minutes: 5,
    max_concurrent_sessions: 3,
    force_relogin_hours: 12
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/security/settings');
      if (data.success && data.data) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch security settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load security settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await apiCall('/api/security/settings', {
        method: 'POST',
        body: JSON.stringify(settings)
      });

      if (response.success) {
        toast({
          title: 'Settings Saved',
          description: 'Security settings have been updated successfully.',
        });
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save security settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: parseInt(value) || 0
    }));
  };

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <span>Only Super Admins can access security settings.</span>
            </div>
          </CardContent>
        </Card>
      </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-violet-600" />
            Session Security Settings
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Configure banking-grade security settings for all users
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
          data-testid="save-security-settings-btn"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Warning Banner */}
      <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-300">
              <p className="font-medium">Important Security Notice</p>
              <p className="mt-1 text-amber-700 dark:text-amber-400">
                Changes to these settings will affect all users. Shorter timeouts increase security but may impact user experience. 
                Changes take effect on next login.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Token Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Token Settings
            </CardTitle>
            <CardDescription>
              Configure access and refresh token expiration times
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="access_token_expiry" className="flex items-center gap-2">
                Access Token Expiry
                <span className="text-xs text-slate-500">(minutes)</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="access_token_expiry"
                  type="number"
                  min="5"
                  max="60"
                  value={settings.access_token_expiry_minutes}
                  onChange={(e) => handleChange('access_token_expiry_minutes', e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-slate-500">5 - 60 minutes</span>
              </div>
              <p className="text-xs text-slate-500">
                Shorter times are more secure but require more frequent token refreshes.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="refresh_token_expiry" className="flex items-center gap-2">
                Refresh Token Expiry
                <span className="text-xs text-slate-500">(hours)</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="refresh_token_expiry"
                  type="number"
                  min="1"
                  max="24"
                  value={settings.refresh_token_expiry_hours}
                  onChange={(e) => handleChange('refresh_token_expiry_hours', e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-slate-500">1 - 24 hours</span>
              </div>
              <p className="text-xs text-slate-500">
                Maximum session duration. Users must re-login after this period.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Idle Timeout Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-orange-600" />
              Idle Timeout
            </CardTitle>
            <CardDescription>
              Lock sessions after period of inactivity
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="idle_timeout" className="flex items-center gap-2">
                Idle Timeout Duration
                <span className="text-xs text-slate-500">(minutes)</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="idle_timeout"
                  type="number"
                  min="5"
                  max="120"
                  value={settings.idle_timeout_minutes}
                  onChange={(e) => handleChange('idle_timeout_minutes', e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-slate-500">5 - 120 minutes</span>
              </div>
              <p className="text-xs text-slate-500">
                Session will be locked (not logged out) after this period of inactivity.
              </p>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  When locked, users enter their password to resume. The refresh token is preserved, 
                  so they don&apos;t need to fully re-login unless the refresh token has expired.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sudo Mode Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-purple-600" />
              Sudo Mode (Privilege Escalation)
            </CardTitle>
            <CardDescription>
              Extra verification for sensitive operations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sudo_expiry" className="flex items-center gap-2">
                Sudo Token Duration
                <span className="text-xs text-slate-500">(minutes)</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="sudo_expiry"
                  type="number"
                  min="2"
                  max="15"
                  value={settings.sudo_token_expiry_minutes}
                  onChange={(e) => handleChange('sudo_token_expiry_minutes', e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-slate-500">2 - 15 minutes</span>
              </div>
              <p className="text-xs text-slate-500">
                After verifying password, users can perform sensitive actions for this duration.
              </p>
            </div>

            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-xs text-purple-700 dark:text-purple-300 font-medium mb-2">
                Protected Operations:
              </p>
              <ul className="text-xs text-purple-600 dark:text-purple-400 space-y-1">
                <li>• Update bank account / payout settings</li>
                <li>• Add or remove team members</li>
                <li>• Export customer data</li>
                <li>• Security-sensitive settings changes</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Session Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-600" />
              Session Management
            </CardTitle>
            <CardDescription>
              Control user session behavior
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="max_sessions" className="flex items-center gap-2">
                Max Concurrent Sessions
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="max_sessions"
                  type="number"
                  min="1"
                  max="10"
                  value={settings.max_concurrent_sessions}
                  onChange={(e) => handleChange('max_concurrent_sessions', e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-slate-500">1 - 10 sessions</span>
              </div>
              <p className="text-xs text-slate-500">
                Maximum number of devices a user can be logged into simultaneously.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="force_relogin" className="flex items-center gap-2">
                Force Re-login After
                <span className="text-xs text-slate-500">(hours)</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="force_relogin"
                  type="number"
                  min="4"
                  max="24"
                  value={settings.force_relogin_hours}
                  onChange={(e) => handleChange('force_relogin_hours', e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-slate-500">4 - 24 hours</span>
              </div>
              <p className="text-xs text-slate-500">
                Users must re-authenticate after this period, regardless of activity.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Security Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Current Security Configuration Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{settings.access_token_expiry_minutes}m</p>
              <p className="text-xs text-slate-500">Access Token</p>
            </div>
            <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{settings.refresh_token_expiry_hours}h</p>
              <p className="text-xs text-slate-500">Refresh Token</p>
            </div>
            <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">{settings.idle_timeout_minutes}m</p>
              <p className="text-xs text-slate-500">Idle Lock</p>
            </div>
            <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{settings.sudo_token_expiry_minutes}m</p>
              <p className="text-xs text-slate-500">Sudo Mode</p>
            </div>
            <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-2xl font-bold text-cyan-600">{settings.max_concurrent_sessions}</p>
              <p className="text-xs text-slate-500">Max Sessions</p>
            </div>
            <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{settings.force_relogin_hours}h</p>
              <p className="text-xs text-slate-500">Force Re-login</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
};

export default AdminSessionSecurity;
